import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../Ui_Components/PageHeader";
import { ReactComponent as VegSvg } from "../../../../assects/svg/Recipe/Veg.svg";
import { ReactComponent as NonVegSvg } from "../../../../assects/svg/Recipe/Non_Veg.svg";
import { ReactComponent as NoImageIcon } from "../../../../assects/svg/food_lib/no-image-view.svg";
import { ToastContainer, useToast } from "../../../Ui_Components/ToastMessage";
import { Button } from "../../../Ui_Components/Button";
import IngredientReasonModal, {
  ReasonModalMode,
} from "../IngredientReasonModal";
import type {
  IngredientActivityAction,
  IngredientDetail,
} from "../types";
import {
  archiveOrRestoreIngredient,
  getIngredientDetails,
} from "../../../../services/ingredients";
import { mapApiToIngredientDetail } from "../ingredientApiMapper";

// Color per macro stat, matches the mockup swatches.
const STAT_COLORS = {
  cal: "#D946A6",
  carb: "#F59E0B",
  netCarb: "#F59E0B",
  fats: "#EC4899",
  apro: "#22B5D8",
  grossPro: "#1F1F1F",
  gl: "#22C55E",
  fiber: "#8B5CF6",
  cpf: "#2563EB",
};

// Micro-nutrient row config. `key` is the backend field name that lands
// in `ingredient.micronutrients`; `label` and `unit` drive the display.
type MicroField = { key: string; label: string; unit: "mcg" | "mg" };
const MICRO_ROWS: MicroField[] = [
  { key: "vitamin_a", label: "Vitamin A", unit: "mcg" },
  { key: "vitamin_d", label: "Vitamin D", unit: "mcg" },
  { key: "vitamin_e", label: "Vitamin E", unit: "mcg" },
  { key: "vitamin_k", label: "Vitamin K", unit: "mcg" },
  { key: "vitamin_c", label: "Vitamin C", unit: "mcg" },
  { key: "thiamine_b1", label: "Thiamine B1", unit: "mg" },
  { key: "riboflavin_b2", label: "Riboflavin B2", unit: "mg" },
  { key: "niacin_b3", label: "Niacin B3", unit: "mg" },
  { key: "pantothenic_acid_b5", label: "Panto. B5", unit: "mg" },
  { key: "vitamin_b6", label: "Vitamin B6", unit: "mg" },
  { key: "biotin_b7", label: "Biotin B7", unit: "mg" },
  { key: "folate_b9", label: "Folate B9", unit: "mg" },
  { key: "vitamin_b12", label: "Vitamin B12", unit: "mcg" },
  { key: "choline", label: "Choline", unit: "mg" },
  { key: "calcium", label: "Calcium", unit: "mg" },
  { key: "phosphorus", label: "Phosphorus", unit: "mg" },
  { key: "magnesium", label: "Magnesium", unit: "mg" },
  { key: "sodium", label: "Sodium", unit: "mg" },
  { key: "potassium", label: "Potassium", unit: "mg" },
  { key: "chloride", label: "Chloride", unit: "mg" },
  { key: "iron", label: "Iron", unit: "mg" },
  { key: "zinc", label: "Zinc", unit: "mg" },
  { key: "copper", label: "Copper", unit: "mg" },
  { key: "manganese", label: "Manganese", unit: "mg" },
  { key: "selenium", label: "Selenium", unit: "mcg" },
  { key: "iodine", label: "Iodine", unit: "mcg" },
  { key: "chromium", label: "Chromium", unit: "mcg" },
  { key: "molybdenum", label: "Molybdenum", unit: "mcg" },
  { key: "fluoride", label: "Fluoride", unit: "mg" },
];

// Trim trailing zeros for a nicer display ("150.00" → "150", "0.05" → "0.05").
const formatMicroValue = (raw: string): string => {
  if (!raw) return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  // parseFloat keeps significant digits without forcing a fixed precision.
  return String(parseFloat(n.toFixed(6)));
};

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] 3xl:text-xs font-inter uppercase tracking-wider text-[#8E8E93]">
      {label}
    </span>
    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
      {value}
    </span>
  </div>
);

const Stat: React.FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
      {label} -
    </span>
    <span
      className="text-sm 3xl:text-base font-semibold font-inter"
      style={{ color }}
    >
      {value}
    </span>
  </div>
);

const ViewIngredientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toasts,
    removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();
  const [reasonMode, setReasonMode] = useState<ReasonModalMode | null>(null);
  const [ingredient, setIngredient] = useState<IngredientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Fetch full detail record whenever the route id changes. The list page
  // only supplies a subset of these fields, so we always hit the detail
  // endpoint rather than trusting nav state.
  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setIngredient(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    (async () => {
      try {
        const api = await getIngredientDetails(id);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === "1" ||
          api?.success === 1 ||
          api?.success === "true";
        if (!isSuccess || !api?.food_data) {
          setIngredient(null);
          setErrorMsg(api?.message || "Failed to load ingredient");
          setLoading(false);
          return;
        }
        setIngredient(mapApiToIngredientDetail(api.food_data));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setIngredient(null);
        setErrorMsg(
          err instanceof Error ? err.message : "Failed to load ingredient",
        );
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* Pick up a deferred success toast from AddIngredientPage after an edit
     save. Ref guards against React 18 StrictMode's double-invoke; nav state
     is cleared so the toast doesn't re-fire on refresh or back-navigation. */
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (toastFiredRef.current) return;
    const state = location.state as
      | { showSuccessToast?: boolean; toastMessage?: string }
      | null;
    if (state?.showSuccessToast) {
      toastFiredRef.current = true;
      showSuccess(state.toastMessage || "Success");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !ingredient) {
    return (
      <div className="h-screen flex flex-col bg-[#F7F7FA]">
        <PageHeader
          title="View ingredients"
          breadcrumb="Ingredients > View ingredients"
          breadcrumbLinks={["/food-library/ingredient"]}
        />
        <div className="flex-1 flex items-center justify-center">
          <p
            className={`font-inter ${
              errorMsg ? "text-red-500" : "text-gray-500"
            }`}
          >
            {loading
              ? "Loading ingredient..."
              : errorMsg || "Ingredient not found."}
          </p>
        </div>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    );
  }

  const isArchived = ingredient.status === "Archived";

  const rightContent = (
    <div className="flex items-center gap-3">
      {isArchived ? (
        <>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setReasonMode("restore")}
          >
            Restore
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() =>
              navigate(`/food-library/ingredient/${ingredient.id}/edit`, {
                state: { ingredient },
              })
            }
          >
            Edit
          </Button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setReasonMode("archive")}
            className="px-5 py-2 border bg-white border-[#F87171] text-[#F87171] hover:bg-[#FEF2F2] transition-colors text-sm 3xl:text-[16px] font-medium font-inter rounded-[6px]"
          >
            Archive
          </button>
          <Button
            variant="primary"
            size="md"
            onClick={() =>
              navigate(`/food-library/ingredient/${ingredient.id}/edit`, {
                state: { ingredient },
              })
            }
          >
            Edit
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
      <PageHeader
        title="View ingredients"
        breadcrumb="Ingredients > View ingredients"
        breadcrumbLinks={["/food-library/ingredient"]}
        rightContent={rightContent}
      />

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-8 custom-scrollbar-overlay">
        {/* Top row: image (spans both right-column cards) + info card + tags card */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] lg:grid-rows-[auto_auto] gap-4">
          {/* Image tile — spans the info + tags cards on the right */}
          <div className="rounded-md overflow-hidden bg-[#E5DFC8] min-h-[200px] lg:min-h-[220px] h-full lg:row-span-2">
            {ingredient.imageUrl ? (
              <img
                src={ingredient.imageUrl}
                alt={ingredient.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <NoImageIcon
                className="w-full h-full"
                preserveAspectRatio="none"
              />
            )}
          </div>

          {/* Info card */}
          <div className="bg-white rounded-md border border-[#F0F0F0] flex flex-col">
            {/* Header row */}
            <div className="px-5 py-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
                  {ingredient.name}
                </span>
                {ingredient.dietType === "veg" && (
                  <VegSvg className="w-4 h-4" />
                )}
                {ingredient.dietType === "nonveg" && (
                  <NonVegSvg className="w-4 h-4" />
                )}
              </div>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs 3xl:text-[14px] font-semibold font-inter ${
                  ingredient.status === "Active"
                    ? "bg-[#EBF7F4] text-[#0DB5B5]"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {ingredient.status}
              </span>
            </div>

            {/* Middle: base qty / created by / category */}
            <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InfoField
                label="BASE QUANTITY"
                value={ingredient.baseQuantity}
              />
              <InfoField label="CREATED BY" value={ingredient.createdBy} />
              <InfoField
                label="CATEGORY"
                value={ingredient.category?.trim() ? ingredient.category : "—"}
              />
            </div>
          </div>

          {/* Tags card — separate card under the info card */}
          <div className="bg-white rounded-md border border-[#F0F0F0] px-5 py-4 flex flex-col gap-2">
            <span className="text-[10px] 3xl:text-xs font-inter uppercase tracking-wider text-[#8E8E93]">
              TAGS
            </span>
            {ingredient.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ingredient.tags.map((tag, idx) => (
                  <span
                    key={`${tag}-${idx}`}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-[#F2F2F2] text-[#1F1F1F] text-xs 3xl:text-sm font-inter"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                —
              </span>
            )}
          </div>
        </div>

        {/* Macro stats card */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] px-5 py-4">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-3">
            Macro
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <Stat
              label="Cal"
              value={String(ingredient.calories)}
              color={STAT_COLORS.cal}
            />
            <Stat
              label="Carb"
              value={`${ingredient.carbs}g`}
              color={STAT_COLORS.carb}
            />
            <Stat
              label="Net Carb"
              value={`${ingredient.netCarbs}g`}
              color={STAT_COLORS.netCarb}
            />
            <Stat
              label="Fats"
              value={`${ingredient.fats}g`}
              color={STAT_COLORS.fats}
            />
            <Stat
              label="A.pro"
              value={`${ingredient.adjProtein}g`}
              color={STAT_COLORS.apro}
            />
            <Stat
              label="Gross pro"
              value={`${ingredient.grossProtein}g`}
              color={STAT_COLORS.grossPro}
            />
            <Stat
              label="GL"
              value={`${ingredient.gl}`}
              color={STAT_COLORS.gl}
            />
            <Stat
              label="Fiber"
              value={`${ingredient.fiber ?? 0}g`}
              color={STAT_COLORS.fiber}
            />
            <Stat
              label="C:P:F"
              value={ingredient.cpfRatio}
              color={STAT_COLORS.cpf}
            />
          </div>
        </div>

        {/* Micro nutrients card */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] px-5 py-4">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
            Micro
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
            {MICRO_ROWS.map((row) => {
              const raw = ingredient.micronutrients?.[row.key] ?? "";
              const display = formatMicroValue(raw);
              return (
                <div key={row.key} className="flex flex-col gap-1">
                  <span className="text-xs 3xl:text-sm font-inter text-[#8E8E93]">
                    {row.label} -
                  </span>
                  <span className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
                    {display === "—" ? display : `${display} ${row.unit}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom row: unit conversions + activity */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Unit conversions (1/3) */}
          <div className="bg-white rounded-md border border-[#F0F0F0] p-5">
            <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
              Unit conversions
            </h3>
            <div className="flex flex-col gap-3">
              {ingredient.unitConversions.map((row, idx) => (
                <div
                  key={`${row.unit}-${idx}`}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                    {row.unit}
                  </span>
                  <span className="text-sm 3xl:text-base font-inter text-[#6B7280]">
                    {row.grams}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Activity (2/3) */}
          <div className="bg-white rounded-md border border-[#F0F0F0] p-5 lg:col-span-2">
            <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
              Activity
            </h3>
            <div className="flex flex-col">
              {ingredient.activity.map((entry, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === ingredient.activity.length - 1;
                const actionLabelMap: Record<IngredientActivityAction, string> =
                  {
                    created: "Created",
                    added: "Added",
                    edited: "Edited",
                    archived: "Archived",
                    restored: "Restored",
                  };
                const actionLabel = actionLabelMap[entry.action];
                const showRemark =
                  (entry.action === "archived" ||
                    entry.action === "restored") &&
                  !!entry.remark;
                return (
                  <div key={entry.id} className="flex gap-3">
                    {/* Timeline column */}
                    <div className="flex flex-col items-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        className="mt-1 shrink-0 block"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7"
                          fill="white"
                          stroke={isFirst ? "#0DB5B5" : "#D1D5DB"}
                          strokeWidth="2"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="3"
                          fill={isFirst ? "#0DB5B5" : "#D1D5DB"}
                        />
                      </svg>
                      {!isLast && (
                        <span className="flex-1 w-0 border-l border-dashed border-[#D1D5DB] my-1" />
                      )}
                    </div>

                    {/* Text column */}
                    <div className={`pb-4 ${isLast ? "pb-0" : ""}`}>
                      <p className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                        {actionLabel} by {entry.actor}
                      </p>
                      <p className="text-xs 3xl:text-sm font-inter text-[#8E8E93] mt-0.5">
                        {entry.date}
                        {entry.time ? ` · ${entry.time}` : ""}
                        {showRemark ? ` · ${entry.remark}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <IngredientReasonModal
        open={reasonMode !== null}
        mode={reasonMode ?? "archive"}
        ingredientName={ingredient.name}
        onClose={() => setReasonMode(null)}
        onConfirm={async (reason) => {
          // On success navigate back to the list with a deferred toast — the
          // item's status has changed so this view is no longer the right
          // landing page (list tab handles both Active and Archived).
          const mode = reasonMode;
          const isRestore = mode === "restore";
          try {
            const api = await archiveOrRestoreIngredient({
              food_id: ingredient.id,
              is_hide: !isRestore,
              reason,
            });
            const isSuccess =
              api?.success === true ||
              api?.success === "1" ||
              api?.success === 1 ||
              api?.success === "true";
            if (!isSuccess) {
              throw new Error(
                api?.message ||
                  (isRestore
                    ? "Failed to restore ingredient"
                    : "Failed to archive ingredient"),
              );
            }
            setReasonMode(null);
            navigate("/food-library/ingredient", {
              state: {
                showSuccessToast: true,
                toastMessage:
                  api?.message ||
                  (isRestore
                    ? "Ingredient restored successfully"
                    : "Ingredient archived successfully"),
              },
            });
          } catch (err) {
            showError(
              isRestore
                ? "Failed to restore ingredient"
                : "Failed to archive ingredient",
              err instanceof Error ? err.message : undefined,
            );
          }
        }}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default ViewIngredientPage;
